import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Building, Scale, Shield, Phone, Mail, FileText, Info } from 'lucide-react';
import { getSaveaPhone, getSaveaPhoneTel } from '../config/phoneConfig';
import { SEOHead } from '../components/SEOHead';

export const TermsOfServicePage = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';
  const country = isSpanish ? 'ES' : 'FR';
  const saveaPhone = getSaveaPhone(country, 'electricity', 'LP');

  if (isSpanish) {
    // VERSION ESPAGNOLE
    return (
      <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
        <SEOHead 
          title="CGU SAVEA – Condiciones Generales de Uso"
          description="Condiciones Generales de Uso de SAVEA - Aviso Legal y Términos del Servicio"
        />
        <section className="py-16 lg:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl lg:text-5xl font-bold text-sage-900 mb-4">
                CGU SAVEA – Aviso Legal
              </h1>
              <div className="text-sm text-sage-500 mt-4">
                Última actualización: 01 Enero 2026
              </div>
            </div>

            {/* Aviso Legal */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5 text-sage-600" />
                  Aviso Legal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sage-700 leading-relaxed">
                  <strong>Editor:</strong> SOLUCIONES INTEGRALES HORIZON, S.L.U., sociedad de responsabilidad limitada unipersonal, 
                  NIF: B23972946, 
                  domicilio social: Calle Radas 57, Ático 1, 08004 Barcelona, España, 
                  correo electrónico: info@savea.info, 
                  teléfono: {saveaPhone}.
                </p>
                <p className="text-sage-700 leading-relaxed mt-4">
                  <strong>Representante Legal y Director de publicación:</strong> Julien Mercier, NIE: Y9783769C, en calidad de Administrador.
                </p>
                <p className="text-sage-700 leading-relaxed mt-4">
                  <strong>Proveedor de alojamiento web:</strong> Emergent Technologies (infraestructura cloud).
                </p>
              </CardContent>
            </Card>

            {/* Definiciones */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Definiciones</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <strong>Sitio:</strong> conjunto de páginas web accesibles en la dirección principal savea.info.
                </div>
                <div>
                  <strong>Servicio:</strong> puesta a disposición de herramientas gratuitas para comparar ofertas de energía e internet y un número telefónico de contacto con especialistas SAVEA.
                </div>
                <div>
                  <strong>Usuario:</strong> toda persona física mayor de edad y capacitada que acceda al Sitio.
                </div>
                <div>
                  <strong>Especialista:</strong> profesional SAVEA debidamente formado en contratos de energía e internet.
                </div>
                <div>
                  <strong>Partenaire:</strong> proveedor de energía o internet con el que se establece contacto a través de nuestros especialistas.
                </div>
              </CardContent>
            </Card>

            {/* Objeto */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Objeto de las CGU</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <p className="text-sage-700 leading-relaxed">
                  Estas CGU regulan exclusivamente el acceso y la utilización del Sitio y del Servicio. No regulan las condiciones de suministro de energía ni la relación contractual que pudiera surgir entre el Usuario y un Partenaire.
                </p>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-sage-600" />
                  Contacto
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sage-700 mb-4 text-sm">
                  Para cualquier pregunta relacionada con el Sitio o las presentes CGU:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-sage-600" />
                    <a href="mailto:info@savea.info" className="text-sage-600 hover:text-sage-800 text-sm">
                      info@savea.info
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-sage-600" />
                    <a href="tel:0034613072408" className="text-sage-600 hover:text-sage-800 text-sm">
                      +34 613 072 408
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  // VERSION FRANÇAISE COMPLÈTE
  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      <SEOHead 
        title="CGU SAVEA – Conditions Générales d'Utilisation"
        description="Conditions Générales d'Utilisation de SAVEA - Mentions légales et conditions du service"
      />
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-sage-900 mb-4">
              CGU SAVEA – Mentions légales
            </h1>
            <div className="text-sm text-sage-500 mt-4">
              Dernière mise à jour : 01 Janvier 2026
            </div>
          </div>

          {/* Mentions légales */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-sage-600" />
                Mentions légales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sage-700 leading-relaxed text-sm">
                <strong>Éditeur :</strong> SOLUCIONES INTEGRALES HORIZON, S.L.U., société à responsabilité limitée unipersonnelle, 
                NIF : B23972946, 
                siège social : Calle Radas 57, Ático 1, 08004 Barcelona, Espagne, 
                courriel : info@savea.info, 
                tél. : {saveaPhone}. Ci-après désignée comme <strong>La Société</strong>.
              </p>
              <p className="text-sage-700 leading-relaxed mt-4 text-sm">
                <strong>Représentant Légal et Directeur de la publication :</strong> Julien Mercier, NIE : Y9783769C, en qualité d'Administrateur.
              </p>
              <p className="text-sage-700 leading-relaxed mt-4 text-sm">
                <strong>Hébergeur :</strong> Emergent Technologies (infrastructure cloud).
              </p>
            </CardContent>
          </Card>

          {/* Définitions */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Définitions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <strong>Site :</strong> l'ensemble des pages web accessibles à l'adresse principale savea.info.
              </div>
              <div>
                <strong>Service :</strong> mise à disposition d'outils gratuits de comparaison des offres d'énergie et internet et d'un numéro de mise en relation téléphonique avec un spécialiste SAVEA.
              </div>
              <div>
                <strong>Utilisateur :</strong> toute personne physique majeure et capable accédant au Site.
              </div>
              <div>
                <strong>Spécialiste :</strong> professionnel SAVEA, dûment formé aux contrats d'énergie et internet, avec lequel l'Utilisateur est mis en relation.
              </div>
              <div>
                <strong>Partenaire :</strong> fournisseur d'énergie ou d'internet avec lequel le contact est établi via nos spécialistes.
              </div>
            </CardContent>
          </Card>

          {/* Objet des CGU */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Objet des CGU</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sage-700 leading-relaxed text-sm">
                Les présentes CGU régissent exclusivement l'accès et l'utilisation du Site et du Service. 
                Elles ne régissent ni les conditions de fourniture d'énergie ni la relation contractuelle qui peut naître entre l'Utilisateur et un Partenaire.
              </p>
            </CardContent>
          </Card>

          {/* Description du Service */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Description du Service</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <h4 className="font-semibold text-sage-800 mb-2">Comparateur :</h4>
                <p className="text-sage-700">
                  La Société agrège et affiche des données publiques ou communiquées par les fournisseurs d'énergie. 
                  Les résultats sont fournis à titre indicatif ; ils ne constituent ni une recommandation personnalisée au sens de l'article L.111-6 du Code de la consommation, ni un conseil financier.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-sage-800 mb-2">Numéro de mise en relation :</h4>
                <ul className="space-y-2 text-sage-700 list-disc pl-5">
                  <li>Numéro non surtaxé ; coût : prix d'un appel local, hors éventuelle surfacturation opérateur.</li>
                  <li>La Société affiche directement sur son Site le numéro de téléphone d'un Partenaire.</li>
                  <li>La Société n'est pas fournisseur d'énergie ; elle ne prend aucune commande et ne perçoit pas de paiements de l'Utilisateur.</li>
                  <li>La Société peut percevoir une commission de ses Partenaires pour chaque contrat souscrit, sans impact sur le prix final proposé à l'Utilisateur.</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Accès au Site */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Accès au Site</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <p className="text-sage-700 leading-relaxed">
                Le Site est normalement accessible 24 h/24 et 7 j/7. La Société se réserve le droit de suspendre, restreindre ou supprimer l'accès à tout moment pour maintenance, mise à jour ou force majeure, sans préavis ni indemnité.
              </p>
            </CardContent>
          </Card>

          {/* Service de comparaison */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Service de comparaison</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <p className="text-sage-700 leading-relaxed">
                Le service de comparaison en ligne proposé par La Société a pour unique objectif de regrouper des informations factuelles sur les offres des fournisseurs d'énergie, d'offrir une présentation simplifiée de ces données, et de fournir des indications générales destinées à mieux orienter les utilisateurs sur le marché de l'énergie.
              </p>
              <p className="text-sage-700 leading-relaxed">
                En utilisant le service proposé par La Société, vous reconnaissez que celui-ci ne prévoit aucun contact supplémentaire, ni conseil personnalisé ou spécifique, de la part d'un représentant de La Société, que ce soit physiquement, par téléphone, par courrier, courriel ou tout autre moyen. Vous acceptez ainsi avoir reçu des informations suffisantes pour effectuer votre choix en toute connaissance de cause. Si toutefois vous ressentez le besoin d'obtenir des informations ou conseils complémentaires, il vous appartient de vous rapprocher directement d'un représentant ou distributeur du fournisseur d'énergie concerné. Vous reconnaissez être seul responsable du choix final de votre fournisseur et libérez La Société de toute responsabilité quant aux conséquences éventuelles liées à ce choix.
              </p>
              <p className="text-sage-700 leading-relaxed">
                La Société met tout en œuvre pour fournir des informations vérifiées concernant les offres et les partenaires fournisseurs d'énergie. Toutefois, vous reconnaissez et acceptez de ne pas tenir La Société responsable de la qualité des informations transmises par ses partenaires, ni d'éventuelles erreurs ou omissions dans les informations relatives aux produits, services ou tarifs affichés, ni d'une indisponibilité temporaire ou définitive des offres présentées.
              </p>
              <p className="text-sage-700 leading-relaxed">
                Les propositions faites par les fournisseurs d'énergie ou leurs intermédiaires ne sont considérées comme valables que si les informations que vous fournissez sont exactes et correspondent précisément à votre situation personnelle. Toute inexactitude, intentionnelle ou non, pourrait entraîner la nullité du contrat souscrit avec le fournisseur d'énergie. Il vous incombe donc de fournir des réponses exactes, sincères et conformes à votre situation réelle.
              </p>
              <p className="text-sage-700 leading-relaxed">
                Par ailleurs, vous êtes entièrement responsable de vérifier que les réponses fournies reflètent fidèlement votre situation avant de demander une mise en relation avec un partenaire. La Société ne peut en aucun cas être tenue responsable des réponses erronées fournies par vos soins.
              </p>
              <p className="text-sage-700 leading-relaxed">
                Le service proposé par La Société a pour vocation de faciliter votre démarche de sélection d'un fournisseur d'énergie. Cependant, les fournisseurs restent libres de choisir leurs clients en fonction de leurs critères propres. Ainsi, certains ou la totalité des partenaires de La Société pourraient être dans l'impossibilité de vous proposer une offre. Vous reconnaissez et acceptez cette éventualité, et vous déchargez La Société de toute responsabilité si aucune offre, ou un nombre limité d'offres, vous est présenté.
              </p>
              <p className="text-sage-700 leading-relaxed">
                Vous reconnaissez et acceptez également que le service de La Société ne prétend pas présenter toutes les offres disponibles sur le marché de l'énergie, ni nécessairement les offres les moins coûteuses, mais simplement un éventail d'offres jugées pertinentes et adaptées selon les besoins exprimés. La Société présente les offres de ses partenaires fournisseurs, lesquelles ne couvrent pas la totalité du marché français.
              </p>
              <p className="text-sage-700 leading-relaxed">
                Enfin, vous reconnaissez que les informations fournies par La Société concernant les fournisseurs d'énergie ne peuvent pas être exhaustives en raison même de la nature du comparateur et de sa technologie. Il vous appartient en conséquence d'examiner attentivement les offres des partenaires avant toute souscription. Quelle que soit votre prise en compte de cette recommandation, vous reconnaissez expressément que La Société ne peut être tenue responsable du choix final que vous faites, et qu'il vous revient d'effectuer toutes les vérifications nécessaires avant de conclure un contrat adapté à vos besoins. La Société n'intervient en aucune manière dans la relation contractuelle que vous établissez avec le fournisseur d'énergie et n'assume, à cet égard, aucune responsabilité.
              </p>
            </CardContent>
          </Card>

          {/* Responsabilité de La Société */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Responsabilité de La Société</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="text-sage-700 leading-relaxed">
                La Société s'efforce d'afficher des données exactes et actualisées, sans garantie de complétude ni d'absence d'erreur. L'Utilisateur doit vérifier auprès du Partenaire la conformité des informations avant toute souscription.
              </p>
              <p className="text-sage-700 leading-relaxed">
                Toute décision de souscription est prise sous la responsabilité exclusive de l'Utilisateur. La Société décline toute responsabilité pour les conséquences financières ou contractuelles liées au choix de l'Utilisateur.
              </p>
              <p className="text-sage-700 leading-relaxed">
                La Société décline toute responsabilité concernant les contenus externes accessibles via des liens ou fournis par les Partenaires.
              </p>
              <p className="text-sage-700 leading-relaxed">
                La Société ne saurait être tenue pour responsable des pertes de profit, d'opportunité, d'activité ou de données résultant de l'utilisation du Site.
              </p>
            </CardContent>
          </Card>

          {/* Obligations de l'Utilisateur */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Obligations de l'Utilisateur</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <p className="text-sage-700 mb-3">
                En utilisant le Site, l'Utilisateur s'engage à :
              </p>
              <ul className="space-y-2 text-sage-700 list-disc pl-5">
                <li>Utiliser le Site conformément aux lois et règlements en vigueur ;</li>
                <li>S'abstenir de toute extraction de données, usage frauduleux ou perturbation du fonctionnement du Site ;</li>
                <li>Assurer la confidentialité de ses équipements et de ses communications ;</li>
                <li>Fournir, lors de l'appel, des informations exactes au Partenaire.</li>
              </ul>
            </CardContent>
          </Card>

          {/* Propriété intellectuelle */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Propriété intellectuelle</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="text-sage-700 leading-relaxed">
                L'ensemble des éléments qui figurent sur le Site sont protégés par la législation française sur le droit d'auteur et le droit des marques.
              </p>
              <p className="text-sage-700 leading-relaxed">
                L'ensemble des éléments du site, les marques, logos, dessins, graphismes, chartes graphiques, icônes, textes, applications, scripts, fonctionnalités, construction et séquencement des formulaires, arborescence, sont la propriété exclusive de l'Editeur. L'accès au Site n'entraîne aucune cession des droits susvisés.
              </p>
              <p className="text-sage-700 leading-relaxed">
                Les droits d'utilisation du Site ne sont concédés que sous forme numérique aux fins de visualisation des pages consultées, à titre personnel, non cessible et non exclusif.
              </p>
              <p className="text-sage-700 leading-relaxed">
                L'Utilisateur s'interdit de copier, reproduire, modifier, distribuer, afficher ou vendre, par quelque procédé ou forme que ce soit, en tout ou partie, tout élément du Site ou se rapportant à celui-ci, par quelque procédé que ce soit, et pour toute autre finalité y compris à titre commercial, sans l'autorisation préalable et écrite de l'Editeur. En cas d'utilisation illégale ou non autorisée du site, l'Editeur se réserve le droit de prendre toute mesure adéquate qu'elle estime nécessaire et, le cas échéant, d'intenter toute action en justice appropriée, et/ou signaler l'infraction aux autorités judiciaires et de police.
              </p>
            </CardContent>
          </Card>

          {/* Données personnelles */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Données personnelles</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="text-sage-700 leading-relaxed">
                La Société met à votre disposition un site de mise en relation avec nos partenaires fournisseurs d'électricité, et ne traite aucune données personnelles dans ce cadre.
              </p>
              <p className="text-sage-700 leading-relaxed">
                Le traitement des données personnelles collectées vous concernant par, sont et seulement, les informations résultant des cookies mis sur le site internet de La Société, qui sont régis par la Politique de cookie distincte. Conformément au Règlement (UE) 2016/679 (RGPD) et à la loi « Informatique & Libertés », l'Utilisateur dispose d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité ; il peut l'exercer à l'adresse : info@savea.info.
              </p>
            </CardContent>
          </Card>

          {/* Sécurité */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Sécurité</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <p className="text-sage-700 leading-relaxed">
                La Société met en œuvre des moyens techniques conformes à l'état de l'art pour sécuriser le Site. L'Utilisateur reconnaît toutefois que l'Internet présente des vulnérabilités et accepte les risques inhérents à toute connexion.
              </p>
            </CardContent>
          </Card>

          {/* Modification des CGU */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Modification des CGU</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <p className="text-sage-700 leading-relaxed">
                La Société peut modifier les CGU à tout moment. La version applicable est celle publiée en ligne au moment de l'accès ou de l'appel. Il appartient à l'Utilisateur de consulter régulièrement les CGU.
              </p>
            </CardContent>
          </Card>

          {/* Nullité partielle */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Nullité partielle</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <p className="text-sage-700 leading-relaxed">
                Si une clause des présentes venait à être déclarée nulle, les autres dispositions demeureraient pleinement applicables.
              </p>
            </CardContent>
          </Card>

          {/* Droit applicable */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Droit applicable – Juridiction compétente</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <p className="text-sage-700 leading-relaxed">
                Les CGU sont régies par le droit français. Tout litige relève de la compétence exclusive des tribunaux matériellement compétents du ressort de la Cour d'appel de Paris, sous réserve d'une disposition impérative contraire.
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-sage-600" />
                Contact
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sage-700 mb-4 text-sm">
                Pour toute question relative au Site ou aux présentes CGU, l'Utilisateur peut écrire à :
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-sage-600" />
                  <a href="mailto:info@savea.info" className="text-sage-600 hover:text-sage-800 text-sm">
                    info@savea.info
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-sage-600" />
                  <a href="tel:0034613072408" className="text-sage-600 hover:text-sage-800 text-sm">
                    +34 613 072 408
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
